/**
 * The funding request PDF's content as plain data.
 *
 * Glenn's Oct 5 2026 ask: pick bundles or steps from a defense plan, pick
 * possible sources of money, get a PDF for an RFP or a contract for
 * services. Pure, like planNeedsCsv.ts, so the grouping, sizes and wording
 * are testable without rendering a PDF. Sizes come from the same helpers the
 * plan panel uses, so a measurement never reads one way on screen and
 * another on paper.
 *
 * It takes `parties` (public names), never `partyContacts`: no email can
 * reach the document because none is ever passed in.
 */
import { featureArea, featureLength } from './planGeometry';
import { formatAreaSize, formatLineLength } from './planFeatureFormat';
import type { FundingMapFeature } from './fundingRequestMap';
import { featureCenter } from './planNeedsCsv';
import { groupFeaturesForList, effectiveBundleLeader, effectiveBundleTeam } from './planBundles';
import { ASSIGNMENT_STATUS_LABEL } from './planAssignmentSummary';
import { KIND_LABEL, STATUS_META } from './fundingLabels';
import { partyDisplayName } from '../components/map/planParties';
import { DEFENSE_STRATEGIES } from '../components/map/defenseStrategies';
import type {
  DefensePlan,
  DefensePlanAssignment,
  DefensePlanFeature,
  FundingSource,
  PlanParty,
} from '../types';

export interface FundingRequestStep {
  /** 1..N in print order; the "#" column and the number on the map. */
  number: number;
  what: string;
  size: string;
  where: string;
  who: string;
  note: string;
}

export interface FundingRequestGroup {
  /** The bundle id, or the feature id for a loose step. */
  key: string;
  /** The bundle name, or the loose step's strategy label. */
  name: string;
  isBundle: boolean;
  steps: FundingRequestStep[];
  cost: number | null;
  costText: string;
}

export interface FundingRequestSource {
  name: string;
  funder: string;
  kindLabel: string;
  statusLabel: string;
  /** Plain words for a source that isn't open; empty when it is. */
  closedNote: string;
  /** Deadline, award, who applies, link and checked date, one per line. */
  lines: string[];
}

export interface FundingRequestModel {
  title: string;
  planTitle: string;
  preparedBy: string;
  dateText: string;
  summary: string;
  bundleCount: number;
  stepCount: number;
  groups: FundingRequestGroup[];
  /** The selected steps in print order, for the map on page one. */
  mapFeatures: FundingMapFeature[];
  totalCost: number | null;
  totalCostText: string;
  sources: FundingRequestSource[];
  otherSource: string;
  footnote: string;
}

export interface BuildFundingRequestInput {
  plan: DefensePlan;
  selectedFeatureIds: string[];
  /** Typed estimates in dollars, keyed by bundle id or loose feature id. */
  costs: Record<string, number | null | undefined>;
  sources: FundingSource[];
  otherSource?: string;
  parties: PlanParty[];
  assignments: DefensePlanAssignment[];
  today?: Date;
}

export function formatUsd(amount: number): string {
  return `$${Math.round(amount).toLocaleString('en-US')}`;
}

function formatDate(date: Date): string {
  return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}

function formatShortDate(timestamp: { toDate: () => Date }): string {
  return timestamp.toDate().toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function strategyLabel(strategy: DefensePlanFeature['strategy']): string {
  return DEFENSE_STRATEGIES[strategy]?.label ?? strategy;
}

export function featureSizeText(feature: DefensePlanFeature): string {
  if (feature.kind === 'line') return formatLineLength(featureLength(feature.coords));
  if (feature.kind === 'area') return formatAreaSize(featureArea(feature.coords));
  return '';
}

function whereOf(feature: DefensePlanFeature): string {
  if (feature.address) return feature.address;
  const center = featureCenter(feature);
  return center ? `${center.lat.toFixed(5)}, ${center.lng.toFixed(5)}` : '';
}

function pluralize(count: number, noun: string): string {
  return `${count} ${noun}${count === 1 ? '' : 's'}`;
}

function toStep(
  number: number,
  feature: DefensePlanFeature,
  partyById: Map<string, PlanParty>,
  assignments: DefensePlanAssignment[],
  leaderPartyId?: string
): FundingRequestStep {
  const links = assignments.filter((a) => a.featureId === feature.id);
  const who =
    links.length === 0
      ? 'Not assigned yet'
      : links
          .map((link) => {
            const party = partyById.get(link.partyId);
            const name = party ? partyDisplayName(party) : 'Unknown party';
            const status = ASSIGNMENT_STATUS_LABEL[link.status] ?? link.status;
            // The bundle's leader reads "Name (leader, Accepted)", matching
            // the "(leader)" tag the bundle row and the CSV use.
            return link.partyId === leaderPartyId
              ? `${name} (leader, ${status})`
              : `${name} (${status})`;
          })
          .join('; ');
  return {
    number,
    what: strategyLabel(feature.strategy),
    size: featureSizeText(feature),
    where: whereOf(feature),
    who,
    note: feature.note ?? '',
  };
}

function toSource(source: FundingSource): FundingRequestSource {
  const status = STATUS_META[source.status];
  const lines: string[] = [];
  lines.push(
    source.deadline
      ? `Deadline: ${formatShortDate(source.deadline)}`
      : (source.deadlineNote ?? 'No deadline given')
  );
  if (source.maxAward != null) {
    lines.push(`Up to ${formatUsd(source.maxAward)} maximum award`);
  }
  if (source.awardNote) lines.push(source.awardNote);
  lines.push(`Who applies: ${source.whoApplies}`);
  lines.push(source.url);
  lines.push(`Checked ${formatShortDate(source.sourceCheckedAt)}`);
  return {
    name: source.name,
    funder: source.funder,
    kindLabel: KIND_LABEL[source.kind],
    statusLabel: status.label,
    closedNote: status.open
      ? ''
      : `${status.label}. Confirm timing with the funder before counting on it.`,
    lines,
  };
}

// With no source picked the document is a scope of work for a vendor or an
// RFP, so neither its title nor its filename should call it a funding request
// (Glenn, Oct 5 2026: "sending the PDF to a vendor or funder, or attaching it
// to an RFP").
export function fundingRequestFilename(
  plan: DefensePlan,
  today = new Date(),
  hasSources = true
): string {
  const kind = hasSources ? 'funding-request' : 'scope-of-work';
  const slug = (plan.title || 'plan')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-+|-+$)/g, '');
  return `REDI-${kind}-${slug}-${today.toISOString().slice(0, 10)}.pdf`;
}

export function buildFundingRequestModel({
  plan,
  selectedFeatureIds,
  costs,
  sources,
  otherSource = '',
  parties,
  assignments,
  today = new Date(),
}: BuildFundingRequestInput): FundingRequestModel {
  const selected = new Set(selectedFeatureIds);
  const partyById = new Map(parties.map((party) => [party.id, party]));
  const costOf = (key: string): number | null => {
    const value = costs[key];
    return typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : null;
  };

  const groups: FundingRequestGroup[] = [];
  const mapFeatures: FundingMapFeature[] = [];
  let nextNumber = 1;
  for (const entry of groupFeaturesForList(plan.features, plan.bundles ?? [])) {
    // A bundle keeps only its checked members; one with none left is dropped.
    const members = entry.type === 'bundle' ? entry.members : [entry.feature];
    const picked = members.filter((feature) => selected.has(feature.id));
    if (picked.length === 0) continue;
    const key = entry.type === 'bundle' ? entry.bundle.id : entry.feature.id;
    const cost = costOf(key);
    // The leader is the bundle's, so it is resolved over ALL members (stored
    // team, else derived), not only the ticked ones.
    const leaderPartyId =
      entry.type === 'bundle'
        ? effectiveBundleLeader(
            entry.bundle,
            effectiveBundleTeam(
              entry.bundle,
              entry.members,
              Object.fromEntries(assignments.map((a) => [a.id, a]))
            )
          )
        : undefined;
    groups.push({
      key,
      name: entry.type === 'bundle' ? entry.bundle.name : strategyLabel(entry.feature.strategy),
      isBundle: entry.type === 'bundle',
      steps: picked.map((feature) => {
        const number = nextNumber++;
        mapFeatures.push({ number, kind: feature.kind, coords: feature.coords });
        return toStep(number, feature, partyById, assignments, leaderPartyId);
      }),
      cost,
      costText: cost === null ? '' : formatUsd(cost),
    });
  }

  const bundleCount = groups.filter((g) => g.isBundle).length;
  const stepCount = groups.reduce((sum, g) => sum + g.steps.length, 0);
  const given = groups.map((g) => g.cost).filter((c): c is number => c !== null);
  const totalCost = given.length > 0 ? given.reduce((sum, c) => sum + c, 0) : null;
  const totalCostText = totalCost === null ? '' : formatUsd(totalCost);

  const summaryParts = [pluralize(stepCount, 'step')];
  if (bundleCount > 0) summaryParts.unshift(pluralize(bundleCount, 'bundle'));
  const summary =
    totalCost === null
      ? summaryParts.join(', ')
      : `${summaryParts.join(', ')}. Estimated total ${totalCostText}`;

  const other = otherSource.trim();
  return {
    title: sources.length > 0 || other ? 'Funding request' : 'Scope of work',
    planTitle: plan.title,
    preparedBy: plan.ownerName,
    dateText: formatDate(today),
    summary,
    bundleCount,
    stepCount,
    groups,
    mapFeatures,
    totalCost,
    totalCostText,
    sources: sources.map(toSource),
    otherSource: other,
    footnote: 'Sizes are computed from the drawn plan. Costs are the applicant’s estimates.',
  };
}
